import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import { Printer, RotateCw, ScanLine, ShieldCheck, MessageSquare, Copy } from "lucide-react";
import { toast } from "sonner";

export default function AccessCards() {
  const [players, setPlayers] = useState([]);
  const [qrMap, setQrMap] = useState({}); // slug -> data URL

  const origin = useMemo(() => window.location.origin, []);

  const load = async () => {
    try {
      const { data } = await api.get("/players", { params: { status_filter: "active" } });
      setPlayers(data);
    } catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { load(); }, []);

  // Regenerate QR codes when players change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      for (const p of players) {
        if (!p.photo_upload_code) continue;
        const url = `${origin}/player/${p.slug}?code=${p.photo_upload_code}`;
        try {
          next[p.slug] = await QRCode.toDataURL(url, {
            errorCorrectionLevel: "M",
            margin: 1,
            width: 240,
            color: { dark: "#0b0b0f", light: "#ffffff" },
          });
        } catch { /* ignore */ }
      }
      if (!cancelled) setQrMap(next);
    })();
    return () => { cancelled = true; };
  }, [players, origin]);

  const rotateAll = async () => {
    if (!confirm("Regenerate upload codes for ALL active players?\n\nEvery current QR code and printed card will stop working immediately. You'll need to reprint and re-share them.")) return;
    try {
      const { data } = await api.post("/players/rotate-all-photo-codes");
      toast.success(`Regenerated codes for ${data.rotated} players`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const print = () => window.print();

  return (
    <div className="p-6 md:p-10" data-testid="access-cards-page">
      {/* Print-only stylesheet */}
      <style>{`
        @media print {
          @page { size: letter; margin: 0.4in; }
          body { background: #fff !important; }
          .no-print { display: none !important; }
          [data-testid="app-sidebar"], [data-testid="mobile-bottom-nav"] { display: none !important; }
          main { margin-left: 0 !important; padding-bottom: 0 !important; }
          .access-cards-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 0.4in !important; }
          .access-card { background: #fff !important; color: #111 !important; border: 1px solid #6B0F1A !important; break-inside: avoid; page-break-inside: avoid; }
          .access-card .card-name { color: #111 !important; }
          .access-card .card-sub { color: #444 !important; }
          .access-card .card-code { color: #6B0F1A !important; }
          .access-card .card-instructions { color: #444 !important; }
          .access-card .card-instructions strong { color: #111 !important; }
          .access-card .card-qr-frame { background: #fff !important; border-color: #ddd !important; }
        }
      `}</style>

      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6 no-print">
        <div>
          <p className="eyebrow flex items-center gap-2"><ScanLine className="w-3.5 h-3.5" /> Photo Access</p>
          <h1 className="heading-1">Player <span className="text-burgundy">Access Cards</span></h1>
          <p className="text-ink-300 text-sm mt-2 max-w-2xl">
            One QR code per player. Print this page, cut the cards, and hand one to each player individually.
            When they scan, they&apos;ll land on their profile with their code pre-filled — one tap to upload their photo.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-ink-700 hover:bg-ink-800" onClick={rotateAll} data-testid="access-rotate-all-btn">
            <RotateCw className="w-4 h-4 mr-2" /> Regenerate All
          </Button>
          <Button className="bg-burgundy hover:bg-burgundy-hover" onClick={print} data-testid="access-print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print Cards
          </Button>
        </div>
      </div>

      {/* Security notice */}
      <div className="no-print mb-6 flex items-start gap-3 bg-ink-800 border border-ink-700/60 rounded-lg p-4">
        <ShieldCheck className="w-5 h-5 text-burgundy shrink-0 mt-0.5" />
        <div className="text-xs text-ink-300">
          <p className="text-white font-semibold uppercase tracking-wider text-[11px] mb-1">How this works</p>
          <p>The QR code embeds the player&apos;s unique 8-character upload code. Anyone with the card can update that player&apos;s photo — treat them like a locker combination. If a card gets lost or leaked, hit <span className="text-white font-mono">Regenerate All</span> (or rotate a single code from the Roster page) to invalidate old cards instantly.</p>
        </div>
      </div>

      {/* Cards grid */}
      <div className="access-cards-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {players.map((p) => {
          const qr = qrMap[p.slug];
          const code = p.photo_upload_code || "—";
          const profileUrl = `${origin}/player/${p.slug}${p.photo_upload_code ? `?code=${p.photo_upload_code}` : ""}`;
          const smsBody = `Hey ${p.player_name.split(" ")[0]}, upload your NPHS Canucks profile photo here: ${profileUrl} — Coach Canada`;
          const smsHref = `sms:?&body=${encodeURIComponent(smsBody)}`;
          const copyLink = async () => {
            try { await navigator.clipboard.writeText(profileUrl); toast.success(`Copied link for ${p.player_name}`); }
            catch { toast.error("Copy failed"); }
          };
          return (
            <div
              key={p.id}
              className="access-card bg-ink-800 rounded-lg border-2 border-burgundy/40 p-5 flex flex-col"
              data-testid={`access-card-${p.slug}`}
            >
              <div className="flex items-center gap-3 pb-3 border-b border-ink-700/50">
                <PlayerAvatar player={p} size="md" />
                <div className="min-w-0 flex-1">
                  <p className="card-sub text-[10px] uppercase tracking-[0.15em] text-ink-400">
                    NPHS Canucks · #{p.jersey_number || "—"} · {p.position || "—"}
                  </p>
                  <p className="card-name font-display text-lg font-black uppercase truncate">{p.player_name}</p>
                </div>
              </div>

              <div className="card-qr-frame mt-4 mx-auto bg-white rounded-md p-2 border border-white/10">
                {qr ? (
                  <img src={qr} alt={`QR for ${p.player_name}`} className="w-40 h-40 block" />
                ) : (
                  <div className="w-40 h-40 flex items-center justify-center text-xs text-ink-500">Generating…</div>
                )}
              </div>

              <div className="mt-4 text-center">
                <p className="card-sub text-[9px] uppercase tracking-[0.2em] text-ink-400">Upload Code</p>
                <p className="card-code font-mono text-2xl font-black tracking-widest text-burgundy mt-0.5" data-testid={`access-code-${p.slug}`}>
                  {code}
                </p>
              </div>

              {/* Digital share buttons (hidden when printing) */}
              <div className="no-print mt-3 grid grid-cols-2 gap-2">
                <a
                  href={smsHref}
                  className="inline-flex items-center justify-center gap-1.5 py-2 rounded-md bg-burgundy hover:bg-burgundy-hover text-white text-[11px] uppercase tracking-wider font-semibold transition"
                  data-testid={`access-sms-${p.slug}`}
                >
                  <MessageSquare className="w-3.5 h-3.5" /> Text Player
                </a>
                <button
                  onClick={copyLink}
                  className="inline-flex items-center justify-center gap-1.5 py-2 rounded-md border border-ink-700 hover:bg-ink-700 text-ink-200 hover:text-white text-[11px] uppercase tracking-wider font-semibold transition"
                  data-testid={`access-copy-${p.slug}`}
                >
                  <Copy className="w-3.5 h-3.5" /> Copy Link
                </button>
              </div>

              <div className="card-instructions mt-4 text-[10px] text-ink-300 leading-relaxed border-t border-ink-700/40 pt-3">
                <p><strong className="text-white">Scan the QR</strong> with your phone camera → your profile opens with the code already in place. Tap the camera icon, pick a photo, crop, done.</p>
                <p className="mt-1">No phone? Go to <span className="font-mono text-white">/player/{p.slug}</span> and enter the code above.</p>
              </div>
            </div>
          );
        })}
      </div>

      {players.length === 0 && (
        <div className="text-center py-16 text-ink-400">No active players yet. Add players from the Roster page.</div>
      )}
    </div>
  );
}
